import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, Image, message, Modal, Pagination, Spin, Tooltip} from 'antd';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import style from 'components/exception/issue/ReportDetail/style.scss';
import {connect, useSelector} from 'react-redux';
import {selectHasSpecifiedPermissionInCurrentApp} from 'utils/selectors/selectors';
import {RolePermission} from 'utils/constants/role-permission';
import {BillingTeamType} from 'utils/constants/billing';
import {orange} from '@ant-design/colors';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon';
import {useTranslation} from 'react-i18next';
import reportEvent, { EVENT_ACTIONS } from 'utils/reportEvent';

const ReportScreenshot = (props) => {
  const {
    reduxState,
    appId,
    crashMap,
  } = props;

  const { t } = useTranslation();

  const currentApp = useMemo(() => reduxState.app.get('current').toJS(), [reduxState.app.get('current')]);
  const hasPermission = useSelector(state => selectHasSpecifiedPermissionInCurrentApp(state, RolePermission.VIEW_DETAIL));
  const { teamType } = currentApp;
  const isTencentApp = teamType === BillingTeamType.TENCENT;
  const currentUser = useMemo(() => reduxState.user.get('current').toJS(), [reduxState.user.get('current')]);
  const { rtx, isSuper: isSuperAdmin } = currentUser;

  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // 缓存最近一次展示的截图数据
  const cacheRef = useRef({ crashId: null, screenshots: null });

  const {
    crashId,
  } = crashMap || {};

  // 当 crashId 变化时，清空缓存
  useEffect(() => {
    if (cacheRef.current.crashId !== crashId) {
      cacheRef.current = { crashId: null, screenshots: null };
    }
  }, [crashId]);

  async function handleViewClick() {
    if (!crashId) {
      message.warning(ze('crashId不存在', 'crashId does not exist'));
      return;
    }

    reportEvent({
      action: EVENT_ACTIONS.CLICK,
      tp1: '上报详情截屏查看',
      tp6: crashId,
    });

    setModalVisible(true);
    setCurrentPage(1);

    // 如果缓存中有当前 crashId 的截图数据，直接使用缓存
    if (cacheRef.current.crashId === crashId && cacheRef.current.screenshots !== null) {
      setScreenshots(cacheRef.current.screenshots);
      return;
    }

    setLoading(true);
    setScreenshots([]);

    try {
      const rsp = await RestHelper.mustPost('/api/crash/fetchSdkScreenshots', {
        appId,
        crashId,
      });
      const { screenshots: screenshotList } = rsp.json.data || {};
      const result = screenshotList || [];
      setScreenshots(result);
      // 缓存结果
      cacheRef.current = { crashId, screenshots: result };
    } catch (e) {
      message.error(ze('获取截图失败', 'Failed to fetch screenshots'));
      setScreenshots([]);
    } finally {
      setLoading(false);
    }
  }

  function handleModalClose() {
    setModalVisible(false);
    setCurrentPage(1);
  }

  function handlePageChange(page) {
    setCurrentPage(page);
  }

  const currentScreenshot = screenshots.length > 0 ? screenshots[currentPage - 1] : null;

  const renderModalContent = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin />
        </div>
      );
    }

    if (screenshots.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          {ze('截图不存在', 'Screenshot does not exist')}
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: screenshots.length > 1 ? '16px' : 0 }}>
          <Image
            src={`data:image/jpeg;base64,${currentScreenshot?.content || ''}`}
            style={{ maxHeight: '60vh', maxWidth: '100%' }}
            alt={currentScreenshot?.fileName || 'screenshot'}
          />
        </div>
        {screenshots.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              current={currentPage}
              total={screenshots.length}
              pageSize={1}
              onChange={handlePageChange}
              showSizeChanger={false}
              simple
            />
          </div>
        )}
      </div>
    );
  };

  let buttonArea;
  if (!hasPermission) {
    buttonArea = <div style={{ color: orange.primary }}>
      <Tooltip
        title={<div>
          <div>{t('common.当前用户角色缺少以下权限')}</div>
          <div>{ ze('查看个人隐私信息', 'View Personal Privacy Info')}</div>
        </div>}
      >{t('common.无权限')}<WrappedTipsIcon /></Tooltip></div>;
  } else if (isTencentApp && !(isSuperAdmin || rtx)) { // 如果是腾讯内部项目，非超管且非RTX用户，禁止查看
    buttonArea = <div style={{ color: orange.primary }}>
      <Tooltip
        title={<div>
          <div>{ ze('非企微用户禁止查看腾讯项目截图', 'Non internal user is not allowed to view Tencent project screenshots') }</div>
        </div>}
      >{t('common.无权限')}<WrappedTipsIcon /></Tooltip></div>;
  } else {
    buttonArea = <Button onClick={handleViewClick}>
      {ze('查看', 'View')}
    </Button>;
  }

  return (
    <ul>
      <li>
        <p className={style.label}>{ze('截屏', 'Screenshot')}</p>
        <p className={style.detail}>
          { buttonArea }
        </p>
      </li>
      <Modal
        title={ze('截屏', 'Screenshot')}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        destroyOnClose
      >
        {renderModalContent()}
      </Modal>
    </ul>
  );
};

const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(ReportScreenshot);
